import { z } from 'zod/v4'

import { defineScreen, type ScreenData, type ScreenQuery } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

/*
 * «Centro de ayuda»: three screens for seven routes. Each view carries only the fields the page and its forms paint,
 * with the dates as ISO text; `shared-help-center-screen.type-assertions.ts` ties them to a `Pick` of
 * `types/help-center.types.ts`, so a field added to one side and not to the other stops the compilation.
 */

export const HELP_VIDEO_PLATFORMS = ['youtube', 'vimeo', 'wistia', 'custom'] as const
export const HELP_RELEASE_TYPES = ['major', 'minor', 'patch', 'hotfix'] as const
export const HELP_RELEASE_NOTE_CHANGE_TYPES = [
  'feature',
  'improvement',
  'bug_fix',
  'security',
  'breaking_change',
] as const

const count = z.number().int().nonnegative()

/**
 * A list as the screen serves it: the rows the read returned (capped) and the `total` the server counts for the same
 * filter, so a view that shows fewer rows than exist can say «N of M» instead of passing a cut list for the whole.
 */
const rowsOf = <Row extends z.ZodType>(row: Row) =>
  z.strictObject({ rows: z.array(row), total: count })

export const helpCenterCountsSchema = z.strictObject({
  articles: count,
  publishedArticles: count,
  categories: count,
  faqs: count,
  publishedFaqs: count,
  videos: count,
  releaseNotes: count,
})

export const helpCenterAnalyticsSchema = z.strictObject({
  totalViews: count,
  helpfulVotes: count,
  totalVotes: count,
  helpfulVotePercent: z.number().int().min(0).max(100).nullable(),
})

/** A category as the article and FAQ rows name it, and as the category selector of the video form offers it. */
export const helpCenterCategoryRefSchema = z.strictObject({
  id: z.string(),
  name: z.string(),
})

export const helpCenterArticleRowSchema = z.strictObject({
  id: z.string(),
  title: z.string(),
  slug: z.string(),
  isPublished: z.boolean(),
  viewCount: count,
  createdAt: z.string(),
  category: helpCenterCategoryRefSchema.nullable(),
})

export const helpCenterCategoryRowSchema = z.strictObject({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  icon: z.string().nullable(),
  isVisible: z.boolean(),
  parentId: z.string().nullable(),
  articleCount: count,
})

export const helpCenterFaqRowSchema = z.strictObject({
  id: z.string(),
  question: z.string(),
  answer: z.string(),
  isPublished: z.boolean(),
  viewCount: count,
  helpfulCount: count,
  category: helpCenterCategoryRefSchema.nullable(),
})

export const helpCenterVideoRowSchema = z.strictObject({
  id: z.string(),
  title: z.string(),
  slug: z.string(),
  videoUrl: z.string(),
  description: z.string().nullable(),
  videoId: z.string().nullable(),
  thumbnailUrl: z.string().nullable(),
  categoryId: z.string().nullable(),
  videoPlatform: z.enum(HELP_VIDEO_PLATFORMS),
  durationSeconds: count,
  tags: z.array(z.string()),
  relatedArticleIds: z.array(z.string()),
  isPublished: z.boolean(),
  isFeatured: z.boolean(),
  sortOrder: z.number().int(),
})

export const helpCenterReleaseNoteChangeSchema = z.strictObject({
  type: z.enum(HELP_RELEASE_NOTE_CHANGE_TYPES),
  text: z.string(),
  scope: z.string().optional(),
})

export const helpCenterReleaseNoteLinksSchema = z.strictObject({
  blog: z.string().optional(),
  github: z.string().optional(),
  documentation: z.string().optional(),
  upgradeGuide: z.string().optional(),
})

export const helpCenterReleaseNoteRowSchema = z.strictObject({
  id: z.string(),
  version: z.string(),
  title: z.string(),
  content: z.string(),
  description: z.string().nullable(),
  releaseType: z.enum(HELP_RELEASE_TYPES),
  changes: z.array(helpCenterReleaseNoteChangeSchema),
  links: helpCenterReleaseNoteLinksSchema,
  releasedAt: z.string(),
  isPublished: z.boolean(),
  isFeatured: z.boolean(),
  hasBreakingChanges: z.boolean(),
  upgradeInfo: z.strictObject({ from: z.string(), notes: z.string() }).nullable(),
})

/**
 * The formats of the videos list filters and of `helpReleaseNoteQuerySchema` (help-center `validators.ts`). `status` instead of `isPublished`: a `true` / `false` text in the URL is not
 * a readable filter.
 */
export const helpCenterVideosQuerySchema = z.strictObject({
  status: z.enum(['published', 'draft']).optional(),
  platform: z.enum(HELP_VIDEO_PLATFORMS).optional(),
  search: z.string().trim().min(1).max(200).optional(),
})

export const helpCenterReleaseNotesQuerySchema = z.strictObject({
  status: z.enum(['published', 'draft']).optional(),
  releaseType: z.enum(HELP_RELEASE_TYPES).optional(),
  search: z.string().trim().min(1).max(200).optional(),
  version: z.string().trim().min(1).max(50).optional(),
  releasedAfter: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  releasedBefore: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
})

export const adminHelpCenterScreen = defineScreen({
  id: 'admin.helpCenter',
  path: '/api/screens/admin/help-center',
  access: 'super_admin',
  query: z.strictObject({}),
  response: z.strictObject({
    counts: screenSection(helpCenterCountsSchema),
    analytics: screenSection(helpCenterAnalyticsSchema),
    articles: screenSection(rowsOf(helpCenterArticleRowSchema)),
    categories: screenSection(rowsOf(helpCenterCategoryRowSchema)),
    faqs: screenSection(rowsOf(helpCenterFaqRowSchema)),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.helpCenter'],
  budget: { requests: 1, statements: 60, bytes: 400_000, p95Ms: 1200 },
  summary: 'Help Center content: tab counts, view analytics, articles, categories and FAQs',
})

export const adminHelpCenterVideosScreen = defineScreen({
  id: 'admin.helpCenter.videos',
  path: '/api/screens/admin/help-center/videos',
  access: 'super_admin',
  query: helpCenterVideosQuerySchema,
  response: z.strictObject({
    counts: screenSection(helpCenterCountsSchema),
    videos: screenSection(rowsOf(helpCenterVideoRowSchema)),
    categoryOptions: screenSection(rowsOf(helpCenterCategoryRefSchema)),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.helpCenter.videos'],
  budget: { requests: 1, statements: 40, bytes: 200_000, p95Ms: 800 },
  summary:
    'Help Center videos: tab counts, the filtered videos and the category options of the video form',
})

export const adminHelpCenterReleaseNotesScreen = defineScreen({
  id: 'admin.helpCenter.releaseNotes',
  path: '/api/screens/admin/help-center/release-notes',
  access: 'super_admin',
  query: helpCenterReleaseNotesQuerySchema,
  response: z.strictObject({
    counts: screenSection(helpCenterCountsSchema),
    releaseNotes: screenSection(rowsOf(helpCenterReleaseNoteRowSchema)),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.helpCenter.releaseNotes'],
  budget: { requests: 1, statements: 40, bytes: 300_000, p95Ms: 800 },
  summary: 'Help Center release notes: tab counts and the filtered release notes',
})

export type HelpCenterCounts = z.infer<typeof helpCenterCountsSchema>
export type HelpCenterAnalytics = z.infer<typeof helpCenterAnalyticsSchema>
export type HelpCenterCategoryRef = z.infer<typeof helpCenterCategoryRefSchema>
export type HelpCenterArticleRow = z.infer<typeof helpCenterArticleRowSchema>
export type HelpCenterCategoryRow = z.infer<typeof helpCenterCategoryRowSchema>
export type HelpCenterFaqRow = z.infer<typeof helpCenterFaqRowSchema>
export type HelpCenterVideoRow = z.infer<typeof helpCenterVideoRowSchema>
export type HelpCenterReleaseNoteRow = z.infer<typeof helpCenterReleaseNoteRowSchema>
export type AdminHelpCenterData = ScreenData<typeof adminHelpCenterScreen>
export type AdminHelpCenterVideosQuery = ScreenQuery<typeof adminHelpCenterVideosScreen>
export type AdminHelpCenterVideosData = ScreenData<typeof adminHelpCenterVideosScreen>
export type AdminHelpCenterReleaseNotesQuery = ScreenQuery<typeof adminHelpCenterReleaseNotesScreen>
export type AdminHelpCenterReleaseNotesData = ScreenData<typeof adminHelpCenterReleaseNotesScreen>
